import { Intent as BlueprintIntent } from '@blueprintjs/core';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  ChipInput,
  LoadingIcon,
  Modal,
  Selector,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  EDIT_SHARED_ORG_ENDPOINT_DIALOG_CANCEL,
  EDIT_SHARED_ORG_ENDPOINT_DIALOG_EMAIL,
  EDIT_SHARED_ORG_ENDPOINT_DIALOG_EMAIL_CHIP,
  EDIT_SHARED_ORG_ENDPOINT_DIALOG_ROLES,
  EDIT_SHARED_ORG_ENDPOINT_DIALOG_ROLES_ITEM,
  EDIT_SHARED_ORG_ENDPOINT_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { handleMutationResult } from 'src/ui/containers/organization/hooks/helpers';
import { useServerRoles } from 'src/ui/containers/organization/hooks/serverRoles';
import { queueMessage } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection,
  useListOrganizationConnectionAccessQuery,
  useSetMemberConnectionRolesMutation,
} from 'src/ui/graph/types';
import { useCurrentConnection } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';

export const EditSharedOrganizationEndpointDialog: React.VFC = () => {
  const { id = '', org = '' } = useParams<{ id?: string; org?: string }>();
  const dispatch = useAppDispatch();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const dialog = useAppSelector((state) => state.ui.dialog);
  let isOpen = false;
  let userId: string | undefined;
  if (
    dialog &&
    dialog.type === DialogType.EDIT_SHARED_ORGANIZATION_ENDPOINT &&
    dialog.data
  ) {
    isOpen = true;
    userId = dialog.data.userId;
  }

  const { connection } = useCurrentConnection(id, org);
  const invalidateConnection = useInvalidateConnection(
    org,
    connection?.id,
    connection?.index
  );

  const {
    data: accessData,
    isLoading: isAccessLoading,
  } = useListOrganizationConnectionAccessQuery(
    graphQLClient,
    { id: connection?.id || '', org_domain: org },
    { enabled: Boolean(connection?.id && org && isOpen) }
  );

  const accessItem = React.useMemo(
    () =>
      accessData?.listOrganizationConnectionAccess?.find(
        (access) => access?.user?.id === userId
      ),
    [accessData, userId]
  );

  const roleConnections = React.useMemo(
    () => (connection ? [connection as Connection] : []),
    [connection]
  );
  const rolesByConnectionId = useServerRoles(
    roleConnections,
    Boolean(isOpen && connection)
  );
  const rolesData = rolesByConnectionId[connection?.id || ''] || {};
  const roleItems = Array.from(rolesData.roles || []).map((role) => ({
    value: role,
    text: role,
  }));

  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);

  const didIsOpenChange = useDidValueChange(isOpen);
  const didIsAccessLoadingChange = useDidValueChange(isAccessLoading);

  React.useEffect(() => {
    if (
      !(didIsOpenChange || didIsAccessLoadingChange) ||
      isAccessLoading ||
      !isOpen
    ) {
      return;
    }

    if (accessItem?.stardog_roles) {
      setSelectedRoles(
        accessItem.stardog_roles.filter((r): r is string => Boolean(r))
      );
    }
  }, [
    accessItem,
    didIsAccessLoadingChange,
    didIsOpenChange,
    isAccessLoading,
    isOpen,
  ]);

  const {
    isLoading,
    mutate: setMemberConnectionRoles,
  } = useSetMemberConnectionRolesMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data, vars) => {
      invalidateConnection();

      const didSucceed = handleMutationResult(
        dispatch,
        data.setMemberConnectionRoles,
        copy.components.endpoints.editSharedOrgEndpointDialog.notifications
          .error,
        vars.input.emails,
        (res) => res.email
      );
      if (!didSucceed) {
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.endpoints.editSharedOrgEndpointDialog.notifications
              .success,
        })
      );
      handleClose();
    },
  });

  const handleClose = () => {
    dispatch(closedDialog());
    setSelectedRoles([]);
  };

  const handleSubmit = () => {
    if (isLoading || !connection?.id || !accessItem?.user?.email) {
      return;
    }

    setMemberConnectionRoles({
      input: {
        connectionId: connection.id,
        emails: [accessItem.user.email],
        org_domain: org,
        stardog_roles: selectedRoles.length ? selectedRoles : undefined,
      },
    });
  };

  const isSubmitDisabled = isLoading;

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={EDIT_SHARED_ORG_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {
              copy.components.endpoints.editSharedOrgEndpointDialog.buttons
                .cancel
            }
          </Button>
          <Button
            disabled={isSubmitDisabled}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={EDIT_SHARED_ORG_ENDPOINT_DIALOG_SUBMIT}
            variant="primary-accent"
          >
            {
              copy.components.endpoints.editSharedOrgEndpointDialog.buttons
                .submit
            }
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      title={copy.components.endpoints.editSharedOrgEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <Body size="md">
          {copy.components.endpoints.editSharedOrgEndpointDialog.description}
        </Body>

        <ChipInput
          chipTestId={EDIT_SHARED_ORG_ENDPOINT_DIALOG_EMAIL_CHIP}
          clearable={false}
          disabled
          label={copy.components.endpoints.inputs.email.label}
          size="md"
          testId={EDIT_SHARED_ORG_ENDPOINT_DIALOG_EMAIL}
          values={accessItem?.user?.email ? [accessItem?.user?.email] : []}
        />

        <Selector.Multi<null, SelectItem, SelectItem[]>
          chipInputProps={{
            className: 'endpoint-dialog-chip-selector',
            clearable: false,
            label: copy.components.endpoints.inputs.roles.shareLabel,
            size: 'md',
            testId: EDIT_SHARED_ORG_ENDPOINT_DIALOG_ROLES,
            values: selectedRoles,
          }}
          creatable
          createText={copy.components.endpoints.inputs.roles.create}
          filterable
          items={roleItems}
          itemTestId={EDIT_SHARED_ORG_ENDPOINT_DIALOG_ROLES_ITEM}
          loading={rolesData.isLoading}
          onSelect={(selected) =>
            setSelectedRoles(selected.map((item) => item.value))
          }
          popoverProps={{
            portalClassName: 'endpoint-dialog-menu-portal',
          }}
          selected={selectedRoles.map((role) => ({ value: role, text: role }))}
        />
      </div>
    </Modal>
  );
};
